/**
 * Narrative prompt templates.
 *
 * Builds system and turn prompts for the narrative agent.
 * Enforces DOS-era dark fantasy voice, 2nd person, GURPS-aware prose.
 */

/** Optimal temperatures per callsite, based on task determinism requirements. */
export const CALLSITE_TEMPERATURES: Record<string, number> = {
  "narrative-turn": 0.8,
  "opening-scene": 0.85,
  "option-generation": 0.7,
  "background-generation": 0.65,
  "character-assignment": 0.3,
  "world-description": 0.75,
  "animation-frame": 0.8,
  "beat-extraction": 0.2,
  "arc-generation": 0.6,
  "memory-compression": 0.2,
  "beat-summary": 0.15,
} as const;

/** Context for building a narrative turn prompt. */
export interface NarrativeContext {
  /** The action the player took (freeform text or selected option). */
  action: string;
  /** GURPS roll results as human-readable text. */
  rollResults: string;
  /** The player character's name. */
  characterName: string;
  /** Current location name. */
  location: string;
  /** Recent narrative history (last 3-5 entries). */
  recentHistory: string[];
  /** Active status effects, pacts, and conditions. */
  activeEffects: string[];
  /** Storyteller pacing directive (deterministic, from dd-engine). */
  narrativeDirective?: string;
  /** Active story arcs formatted for context injection. */
  activeArcs?: string;
  /** Immediate memory: last 5 full beats, formatted. */
  immediateMemory?: string;
  /** Recent memory: 20 one-line summaries, formatted. */
  recentMemory?: string;
  /** Deep memory: 30 compressed key facts, formatted. */
  deepMemory?: string;
}

/**
 * Formalized consequence tag vocabulary.
 *
 * Every consequence in a beat must use one of these exact prefixes.
 * Agents and downstream parsers rely on this vocabulary for structured extraction.
 */
const CONSEQUENCE_TAG_VOCABULARY = [
  "`owes_favor:target` — social debt created",
  "`promised:target:what` — commitment made",
  "`bound_to:entity` — pact or binding",
  "`debt:target:amount` — material debt",
  "`discovered:what` — new knowledge gained",
  "`injured:bodypart:severity` — physical harm (minor/moderate/severe)",
  "`reputation:faction:+n/-n` — faction standing change",
  "`learned:skill_or_lore` — new skill/knowledge exposure",
  "`lost:item` — item lost or destroyed",
  "`gained:item` — item acquired",
  "`status:effect:duration` — status effect applied (poisoned, blessed, cursed)",
  "`relationship:npc:change` — relationship shift (ally/enemy/neutral)",
].join("\n- ");

/** Build the narrative agent system prompt. */
export function narrativeSystemPrompt(): string {
  return [
    "You are the narrator of Dreadful Depths, a dark fantasy roguelike RPG",
    "set in a grim, low-magic world of dungeons, cursed forests, and",
    "crumbling cities.",
    "",
    "### Voice",
    '- Write in 2nd person present tense ("You step into the gloom...")',
    "- Channel a DOS-era text adventure narrator: terse, atmospheric, gritty",
    "- Favor short, punchy sentences. No purple prose. No modern slang.",
    "- Convey danger, dread, and dark humor in equal measure",
    "- Let silence and implication do the heavy lifting",
    "",
    "### Difficulty Calibration",
    "- Match challenges to the character's capabilities. A beginning adventurer",
    "  (stats around 10) should face challenges they can realistically overcome.",
    "- Reserve deadly encounters, powerful monsters, and impossible odds for",
    "  experienced characters with high stats and many skills.",
    "- GURPS uses 3d6 rolls against effective skill level. The average roll is 10.5.",
    "  A skill at level 10 succeeds about 50% of the time. A skill at level 12",
    "  succeeds about 75% of the time.",
    "- Present situations where the character's BEST skills are relevant. A thief",
    "  should encounter locks and guards, not arcane puzzles. A warrior should face",
    "  fights they can win, not ancient dragons.",
    "- When roll results show failure, narrate a setback — NOT a catastrophe.",
    "  A failed Stealth roll means someone notices you, not that you fall off a cliff.",
    "  A failed combat roll means you miss or get pushed back, not that you lose a limb.",
    "- Successes should feel rewarding. Failures should feel recoverable.",
    "",
    "### Scene Progression",
    "- Each turn MUST move the story forward significantly. Do not describe the",
    "  character taking a single step, looking at one wall, or making one small",
    "  observation.",
    "- Each response should advance the situation to a NEW STATE: a new location",
    "  reached, an encounter resolved, a relationship changed, a secret discovered,",
    "  a deal struck, or a threat neutralized.",
    "- Think in scenes, not in moments. When the player acts, the scene resolves",
    "  and transitions. Something is different at the end of every turn.",
    "- NPCs should react, events should conclude, doors should open or close.",
    "  The world moves forward, it does not wait.",
    "",
    "### Mechanics Awareness",
    "- GURPS mechanics run in the background. You receive roll results.",
    "- NEVER mention dice, numbers, skill levels, or game mechanics directly",
    "- Translate success/failure into narrative consequences naturally",
    "- A critical success should feel earned and vivid",
    "- A critical failure should feel devastating but fair",
    "",
    "### Format",
    "- Keep responses to 2-4 paragraphs (80-200 words typical)",
    "- End on a moment that invites player action",
    "- When describing new locations, set the scene before presenting danger",
    "- Reference the player's recent history to maintain continuity",
    "",
    "### Restrictions",
    "- No meta-commentary or breaking the 4th wall",
    "- No content warnings or disclaimers",
    "- No emoji or markdown formatting",
    "- ASCII-safe characters only (no Unicode beyond basic Latin)",
  ].join("\n");
}

/** Build a narrative turn prompt with full context. */
export function narrativeTurnPrompt(ctx: NarrativeContext): string {
  const parts: string[] = [];

  // Storyteller pacing directive (deterministic, from dd-engine)
  if (ctx.narrativeDirective) {
    parts.push(`### Narrator Directive\n${ctx.narrativeDirective}`);
    parts.push("");
  }

  // Deep memory — persistent world facts
  if (ctx.deepMemory) {
    parts.push("### Key Facts");
    parts.push(ctx.deepMemory);
    parts.push("");
  }

  // Recent memory — compressed turn summaries
  if (ctx.recentMemory) {
    parts.push("### Earlier Events");
    parts.push(ctx.recentMemory);
    parts.push("");
  }

  // Immediate memory — last few beats (preferred over recentHistory)
  if (ctx.immediateMemory) {
    parts.push("### Recent Events");
    parts.push(ctx.immediateMemory);
    parts.push("");
  } else if (ctx.recentHistory.length > 0) {
    parts.push("### Recent Events");
    for (const entry of ctx.recentHistory) {
      parts.push(`- ${entry}`);
    }
    parts.push("");
  }

  // Active story arcs
  if (ctx.activeArcs) {
    parts.push("### Active Story Threads");
    parts.push(ctx.activeArcs);
    parts.push("");
  }

  parts.push(`LOCATION: ${ctx.location}`);
  parts.push(`CHARACTER: ${ctx.characterName}`);

  if (ctx.activeEffects.length > 0) {
    parts.push(`ACTIVE EFFECTS: ${ctx.activeEffects.join(", ")}`);
  }

  parts.push("");
  parts.push(`PLAYER ACTION: ${ctx.action}`);
  parts.push(`ROLL RESULTS: ${ctx.rollResults}`);
  parts.push("");
  parts.push("Narrate the outcome of this action. Stay in voice. End with a moment");
  parts.push("that naturally leads to the player's next decision.");

  return parts.join("\n");
}

/** Build a prompt to extract a structured beat from narrative output. */
export function beatExtractionPrompt(narrative: string, action: string, location: string): string {
  return [
    "Extract a structured summary from this RPG turn.",
    "",
    "### Context",
    `- **Action**: ${action}`,
    `- **Location**: ${location}`,
    "",
    "### Narrative",
    narrative,
    "",
    "### Consequence Tags (use these exact prefixes)",
    `- ${CONSEQUENCE_TAG_VOCABULARY}`,
    "",
    "### Required JSON Schema",
    "```json",
    "{",
    '  "outcome": string,        // one sentence, past tense, factual',
    '  "consequences": string[], // tag:detail format (see vocabulary above)',
    '  "mood": string,           // exactly one word',
    '  "npcs_involved": string[],',
    '  "arcs_advanced": string[], // "arc_id:+n" format, empty if none',
    '  "tags": string[]          // 1-3 from: combat, social, exploration, discovery, progress, setback, obligation',
    "}",
    "```",
    "",
    "### Examples",
    "",
    "Example 1 (combat outcome):",
    '{"outcome":"You dispatched the cave troll with a desperate thrust through its eye","consequences":["injured:left_arm:minor","reputation:miners_guild:+1","gained:troll_fang"],"mood":"triumphant","npcs_involved":[],"arcs_advanced":["mine_infestation:+1"],"tags":["combat","progress"]}',
    "",
    "Example 2 (social encounter):",
    '{"outcome":"The merchant grudgingly agreed to extend your credit after you mentioned the baron\'s name","consequences":["owes_favor:merchant_grynn","reputation:merchant_quarter:+1","debt:grynn:15_silver"],"mood":"tense","npcs_involved":["Grynn"],"arcs_advanced":[],"tags":["social","obligation"]}',
    "",
    "### Rules",
    "- outcome: single sentence, past tense, factual",
    "- consequences: use ONLY the tag prefixes listed above",
    "- mood: exactly one word",
    "- tags: pick 1-3 from the list above",
    "- arcs_advanced: empty array if no story arc was advanced",
    "- Return ONLY the JSON object, no markdown fences",
  ].join("\n");
}

/** Build a prompt to generate initial story arcs for a new world. */
export function arcGenerationPrompt(setting: string, genre: string, tone: string): string {
  return [
    "Generate 3-5 story arcs for a roguelike RPG world.",
    "",
    "### World Context",
    `- **Setting**: ${setting}`,
    `- **Genre**: ${genre}`,
    `- **Tone**: ${tone}`,
    "",
    "### Purpose",
    "Story arcs are running storylines that exist in the world. The player can",
    "discover and interact with them, but is NEVER forced to pursue any. They",
    "are background events that progress whether the player engages or not.",
    "",
    "### Requirements",
    "- At least one exploration/discovery arc (dungeon, ruins, mystery)",
    "- At least one social/political arc (faction conflict, conspiracy)",
    "- At least one survival/environmental arc (threat, resource crisis)",
    "- Each arc should have clear stakes (what happens if no one intervenes)",
    "- Arcs should be interconnected where natural",
    "",
    "### Required JSON Schema",
    "Return ONLY a JSON array of arc objects:",
    "```json",
    "[",
    "  {",
    '    "id": string,                    // snake_case_id',
    '    "title": string,                 // Human Readable Title',
    '    "description": string,           // 2-3 sentences describing what is happening',
    '    "stakes": string,                // What happens if no one intervenes',
    '    "initial_visibility": string,    // "unknown" | "rumored" | "known"',
    '    "initial_tension": number,       // 0.0 to 1.0',
    '    "decay_behavior": string,        // "escalate" | "fade" | "freeze"',
    '    "related_locations": string[],   // location names',
    '    "advancement_triggers": string[] // actions that would advance this',
    "  }",
    "]",
    "```",
    "",
    "### Example",
    '{"id":"flooded_mines","title":"The Drowning Below","description":"Water seeps up from the abandoned iron mines east of town. Something down there cracked the aquifer. The lower tunnels are flooding fast.","stakes":"The mines flood completely within weeks, poisoning the town well and releasing whatever lurks in the deep shafts.","initial_visibility":"rumored","initial_tension":0.3,"decay_behavior":"escalate","related_locations":["Iron Mines","Town Well"],"advancement_triggers":["explore the mines","investigate the flooding","talk to the old miners"]}',
  ].join("\n");
}

/** Build a prompt to compress recent turn summaries into key facts. */
export function memoryCompressionPrompt(summaries: string[]): string {
  const joined = summaries.map((s) => `- ${s}`).join("\n");
  return [
    "Summarize these RPG turn summaries into 3-5 key facts that matter",
    "for future narrative.",
    "",
    "### Recent Turns",
    joined,
    "",
    "### Focus On",
    "- Unresolved obligations (debts, promises, threats)",
    "- Changed relationships (allies gained, enemies made)",
    "- Discovered information (secrets, locations, lore)",
    "- Consequences still in play (injuries, reputation changes)",
    "",
    "### Required JSON Schema",
    "Return ONLY a JSON array of strings:",
    "```json",
    '["Key fact 1", "Key fact 2", ...]',
    "```",
    "",
    "Each fact: one sentence, present tense, specific.",
  ].join("\n");
}

/** Build a prompt to generate a one-line summary of a turn. */
export function beatSummaryPrompt(action: string, outcome: string): string {
  return [
    "Write a single-sentence summary of this RPG turn.",
    "",
    "### Context",
    `- **Action**: ${action}`,
    `- **Outcome**: ${outcome}`,
    "",
    "Return ONLY one sentence, past tense, 15-25 words. No JSON, no quotes.",
  ].join("\n");
}

/**
 * Build a combined narrative + beat extraction prompt.
 *
 * Merges narrative generation and beat extraction into a single LLM call.
 * The LLM outputs narrative prose, then wraps the JSON beat object in
 * `<output>` tags, eliminating the need for a separate beat extraction call.
 * Falls back to `---BEAT---` parsing for in-flight compatibility.
 */
export function narrativeWithBeatPrompt(ctx: NarrativeContext): string {
  // Reuse the existing turn prompt for the narrative portion
  const narrativePart = narrativeTurnPrompt(ctx);

  const beatInstructions = [
    "",
    "ADDITIONAL TASK:",
    "After your narrative, output the beat summary wrapped in output tags.",
    "",
    "### Consequence Tags (use these exact prefixes)",
    `- ${CONSEQUENCE_TAG_VOCABULARY}`,
    "",
    "### Required JSON Schema",
    "```json",
    "{",
    '  "outcome": string,        // one sentence, past tense, factual',
    '  "consequences": string[], // tag:detail format (see vocabulary above)',
    '  "mood": string,           // exactly one word',
    '  "npcs_involved": string[],',
    '  "arcs_advanced": string[], // "arc_id:+n" format, empty if none',
    '  "tags": string[]          // 1-3 from: combat, social, exploration, discovery, progress, setback, obligation',
    "}",
    "```",
    "",
    "### Examples",
    "",
    "Example 1 (combat outcome):",
    '{"outcome":"You dispatched the cave troll with a desperate thrust through its eye","consequences":["injured:left_arm:minor","reputation:miners_guild:+1","gained:troll_fang"],"mood":"triumphant","npcs_involved":[],"arcs_advanced":["mine_infestation:+1"],"tags":["combat","progress"]}',
    "",
    "Example 2 (social encounter):",
    // eslint-disable-next-line quotes
    '{"outcome":"The merchant grudgingly agreed to extend your credit after you mentioned the baron\'s name","consequences":["owes_favor:merchant_grynn","reputation:merchant_quarter:+1","debt:grynn:15_silver"],"mood":"tense","npcs_involved":["Grynn"],"arcs_advanced":[],"tags":["social","obligation"]}',
    "",
    "BEAT RULES:",
    "- outcome: single sentence, past tense, factual",
    "- consequences: use ONLY the tag prefixes listed above",
    "- mood: exactly one word",
    "- tags: pick 1-3 from the list above",
    "- arcs_advanced: empty array if no story arc was advanced",
    "- No markdown fences inside the output tags",
    "",
    "FORMAT: Write your narrative prose first, then on a new line output:",
    "<output>",
    "{JSON beat object here}",
    "</output>",
    "",
    "Legacy format ---BEAT--- is also accepted but <output> tags are preferred.",
  ].join("\n");

  return narrativePart + beatInstructions;
}

/**
 * Parse a combined narrative+beat response.
 *
 * Splits on `<output>` tags (preferred) or the legacy `---BEAT---` separator.
 * If neither is found, the full text is returned as narrative with null beat.
 */
export function parseCombinedResponse(response: string): {
  narrative: string;
  beatJson: string | null;
} {
  // Try <output> tags first (preferred format)
  const outputStart = response.indexOf("<output>");
  if (outputStart !== -1) {
    const narrative = response.substring(0, outputStart).trim();
    const afterTag = response.substring(outputStart + "<output>".length);
    const outputEnd = afterTag.indexOf("</output>");
    const beatRaw = outputEnd !== -1 ? afterTag.substring(0, outputEnd).trim() : afterTag.trim();

    return {
      narrative,
      beatJson: beatRaw.length > 0 ? beatRaw : null,
    };
  }

  // Fall back to legacy ---BEAT--- separator
  const separatorIndex = response.indexOf("---BEAT---");
  if (separatorIndex === -1) {
    return { narrative: response.trim(), beatJson: null };
  }

  const narrative = response.substring(0, separatorIndex).trim();
  const beatRaw = response.substring(separatorIndex + "---BEAT---".length).trim();

  return {
    narrative,
    beatJson: beatRaw.length > 0 ? beatRaw : null,
  };
}

/**
 * Build a continuation prompt for streaming mid-narrative.
 * Used when the typewriter needs more text from an interrupted stream.
 */
export function narrativeContinuationPrompt(lastText: string, characterName: string): string {
  return [
    `Continue the narrative for ${characterName} from where it left off.`,
    "Do not repeat what was already said. Pick up mid-thought if needed.",
    "",
    "### Last Text",
    lastText.slice(-500),
  ].join("\n");
}
